import React, { createContext, useContext, useState, useCallback } from 'react';
import { contracts as initialContracts } from '../data/contracts';
import { toast } from 'react-hot-toast';

export interface Contract {
  id: string;
  name: string;
  category: string;
  description: string;
  price: {
    min: number;
    max: number;
  };
  processingTime: string;
  keyPoints: string[];
  useCases: string[];
  relatedDocuments: string[];
}

interface ContractContextType {
  contracts: Contract[];
  updateContract: (contract: Contract) => void;
  deleteContract: (contractId: string) => void;
  addContract: (contract: Omit<Contract, 'id'>) => void;
}

const ContractContext = createContext<ContractContextType | undefined>(undefined);

export const useContracts = () => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error('useContracts must be used within a ContractProvider');
  }
  return context;
};

export const ContractProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [contracts, setContracts] = useState<Contract[]>(() => {
    try {
      const savedContracts = localStorage.getItem('contracts');
      // Return initial contracts if savedContracts is null, undefined, or the string 'undefined'
      if (!savedContracts || savedContracts === 'undefined') {
        return initialContracts;
      }
      // Parse the saved contracts and validate that it's an array
      const parsedContracts = JSON.parse(savedContracts);
      return Array.isArray(parsedContracts) ? parsedContracts : initialContracts;
    } catch (error) {
      // If there's any error parsing the JSON, return initial contracts
      console.error('Error loading contracts from localStorage:', error);
      return initialContracts;
    }
  });

  // Save contracts to localStorage whenever they change
  const saveContracts = useCallback((newContracts: Contract[]) => {
    try {
      localStorage.setItem('contracts', JSON.stringify(newContracts));
      setContracts(newContracts);
    } catch (error) {
      console.error('Error saving contracts to localStorage:', error);
      toast.error('Failed to save contracts');
    }
  }, []);

  const updateContract = useCallback((updatedContract: Contract) => {
    saveContracts(prevContracts =>
      prevContracts.map(contract =>
        contract.id === updatedContract.id ? updatedContract : contract
      )
    );
    toast.success('Contract updated successfully');
  }, [saveContracts]);

  const deleteContract = useCallback((contractId: string) => {
    saveContracts(prevContracts =>
      prevContracts.filter(contract => contract.id !== contractId)
    );
    toast.success('Contract deleted successfully');
  }, [saveContracts]);

  const addContract = useCallback((newContract: Omit<Contract, 'id'>) => {
    const contract: Contract = {
      ...newContract,
      id: `contract-${Date.now()}`,
    };
    saveContracts(prevContracts => [...prevContracts, contract]);
    toast.success('Contract added successfully');
  }, [saveContracts]);

  return (
    <ContractContext.Provider value={{ contracts, updateContract, deleteContract, addContract }}>
      {children}
    </ContractContext.Provider>
  );
};